import { CircularProgress, Grid } from '@mui/material';
import { validateEmail, validatePhoneNumber } from '@nx-smartmonkey/shared/helpers';
import {
  HSpacer,
  RoutalDialog,
  RoutalInputPhone,
  RoutalInputPhoneValue,
  RoutalLoader,
  RoutalPalette,
  VSpacer,
} from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import { Language } from '@nx-smartmonkey/shared/domain';
import { currencyToSymbol, totalPriceWithDiscount } from '../../../../constants/Pricing';
import { intl } from '../../../../helpers/IntGlobalProvider';
import { useRetrieveOrganization } from '../../../../hooks/organization/useRetrieveOrganization';
import { useRetrieveOrganizationResources } from '../../../../hooks/organization/useRetrieveOrganizationResources';
import { useRetrieveActiveProject } from '../../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveSubscriptionPricing } from '../../../../hooks/subscription/useRetrieveSubscriptionPricing';
import { UseCreateSupervisorProps, useCreateSupervisor } from '../../../../hooks/supervisor/useCreateSupervisor';
import Button from '../../../../react-components/Button';
import InputText from '../../../../react-components/InputText';
import Text from '../../../../react-components/Text';

interface CreateSupervisorDialogProps {
  onClose: (created?: boolean) => void;
}

const defaultSupervisorData = {
  email: ``,
  short_id: ``,
  name: ``,
  company: ``,
} as UseCreateSupervisorProps;

export const CreateSupervisorDialog = ({ onClose }: CreateSupervisorDialogProps) => {
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: organization } = useRetrieveOrganization();
  const { data: organizationResources } = useRetrieveOrganizationResources();
  const { data: pricing, isLoading: gettingPricing } = useRetrieveSubscriptionPricing({
    billingPeriod: organization?.subscription.billing_period,
  });
  const { run: createSupervisor, isLoading: isCreatingSupervisors, success } = useCreateSupervisor();
  const [supervisorData, setSupervisorData] = useState<UseCreateSupervisorProps>(defaultSupervisorData);

  useEffect(() => {
    if (success) {
      setSupervisorData(defaultSupervisorData);
      onClose(true);
    }
  }, [success, onClose]);

  if (!activeProject || !organization || !organizationResources) {
    return null;
  }

  const getTotalPrice = () => {
    if (!pricing) return 0;

    const paidSupervisors = Math.max(1 - pricing.missing_extra.supervisors, 0);

    return totalPriceWithDiscount({
      price: paidSupervisors * pricing.supervisors,
      amountOff: pricing.discount?.amount_off,
      percentOff: pricing.discount?.percent_off,
    });
  };

  const isFormValid = () => {
    if (!supervisorData.email || !validateEmail(supervisorData.email)) {
      return false;
    }

    if (!supervisorData.short_id) {
      return false;
    }

    if (!supervisorData.name) {
      return false;
    }

    if (!supervisorData.company) {
      return false;
    }

    if (supervisorData.phone && !validatePhoneNumber(supervisorData.phone)) {
      return false;
    }

    return true;
  };

  const isConfirmButtonDisabled = () => {
    return isCreatingSupervisors || !isFormValid() || organizationResources.exceedsSupervisorsHardLimit(1);
  };

  return (
    <RoutalDialog
      open
      onCloseDialog={() => onClose()}
      dialogProps={{
        maxWidth: `lg`,
        disableEscapeKeyDown: true,
        disableBackdropClick: true,
      }}
      contentStyles={{
        padding: 0,
      }}
      titleContent={<Text variant="h6" intlId="supervisors.create_supervisor" />}
      dialogContentWarning={<div />}
      showFooter={false}
    >
      <Grid container style={{ height: `600px` }}>
        {gettingPricing ? (
          <Grid
            item
            container
            direction="column"
            style={{ height: `100%`, width: `100%` }}
            alignItems="center"
            justifyContent="center"
          >
            <RoutalLoader />
          </Grid>
        ) : (
          <>
            {pricing ? (
              <>
                <Grid item xs={8} container style={{ padding: `20px 40px` }} direction="column">
                  <InputText
                    id="email"
                    type="email"
                    label={<Text tagName="span" variant="label" intlId="supervisors.create_supervisor.email" />}
                    placeholderIntlId="supervisors.create_supervisor.email"
                    fieldValue={supervisorData.email}
                    onChange={(mail: any) => setSupervisorData({ ...supervisorData, email: mail })}
                    size="medium"
                    variant="outlined"
                    inputProps={{
                      style: {
                        minHeight: `24px`,
                      },
                    }}
                    required
                    validators={[
                      {
                        errorMessage: (
                          <Text
                            tagName="span"
                            style={{ color: RoutalPalette.error.medium }}
                            intlId="settings.error.invalid_email"
                          />
                        ),
                        isInvalid: (text?: string) => !text || !validateEmail(text),
                      },
                    ]}
                  />

                  <VSpacer medium />

                  <InputText
                    type="text"
                    label={<Text tagName="span" variant="label" intlId="supervisors.create_supervisor.short_id" />}
                    inputContainerStyles={{ width: `100%` }}
                    placeholderIntlId="supervisors.create_supervisor.short_id"
                    fieldValue={supervisorData.short_id}
                    onChange={(value: any) => {
                      setSupervisorData({ ...supervisorData, short_id: value });
                    }}
                    required
                    validators={[
                      {
                        errorMessage: (
                          <Text
                            tagName="span"
                            style={{ color: RoutalPalette.error.medium }}
                            intlId="settings.error.field_mandatory"
                          />
                        ),
                        isInvalid: (text?: string) => (text ?? ``) === ``,
                      },
                    ]}
                  />

                  <VSpacer medium />

                  <InputText
                    type="text"
                    label={<Text tagName="span" variant="label" intlId="supervisors.create_supervisor.name" />}
                    inputContainerStyles={{ width: `100%` }}
                    placeholderIntlId="supervisors.create_supervisor.name"
                    fieldValue={supervisorData.name}
                    onChange={(value: any) => {
                      setSupervisorData({ ...supervisorData, name: value });
                    }}
                    required
                    validators={[
                      {
                        errorMessage: (
                          <Text
                            tagName="span"
                            style={{ color: RoutalPalette.error.medium }}
                            intlId="settings.error.field_mandatory"
                          />
                        ),
                        isInvalid: (text?: string) => (text ?? ``) === ``,
                      },
                    ]}
                  />

                  <VSpacer medium />

                  <InputText
                    type="text"
                    label={<Text tagName="span" variant="label" intlId="supervisors.create_supervisor.company" />}
                    inputContainerStyles={{ width: `100%` }}
                    placeholderIntlId="supervisors.create_supervisor.company"
                    fieldValue={supervisorData.company}
                    onChange={(value: any) => {
                      setSupervisorData({ ...supervisorData, company: value });
                    }}
                    required
                    validators={[
                      {
                        errorMessage: (
                          <Text
                            tagName="span"
                            style={{ color: RoutalPalette.error.medium }}
                            intlId="settings.error.field_mandatory"
                          />
                        ),
                        isInvalid: (text?: string) => (text ?? ``) === ``,
                      },
                    ]}
                  />

                  <VSpacer medium />

                  <Grid item container direction="column">
                    <Text variant="label" intlId="supervisors.create_supervisor.phone" />

                    <VSpacer small />

                    <RoutalInputPhone
                      id="supervisor-phone"
                      countryCallingCodeEditable={false}
                      focusInputOnCountrySelection
                      international
                      placeholder={intl.formatMessage({ id: `supervisors.create_supervisor.phone` })}
                      defaultCountry={
                        activeProject?.location?.country_code
                          ? activeProject?.location?.country_code.toUpperCase()
                          : Language.getDefaultLanguage().value
                      }
                      value={supervisorData.phone as RoutalInputPhoneValue}
                      onChange={(value?: RoutalInputPhoneValue) => {
                        setSupervisorData({ ...supervisorData, phone: value ?? `` });
                      }}
                    />
                  </Grid>
                </Grid>

                <Grid
                  item
                  xs={4}
                  container
                  style={{
                    backgroundColor: RoutalPalette.grey9,
                    padding: `20px 40px`,
                  }}
                  direction="column"
                  alignItems="center"
                  justifyContent="center"
                >
                  {organization.subscription.status !== `trialing` && (
                    <>
                      <Grid item>
                        {organization.subscription.has_contract ? (
                          organizationResources.exceedsSupervisorsHardLimit(1) ? (
                            <Text
                              variant="body-medium"
                              intlId={`supervisors.create_supervisor.contract_exceeded`}
                              style={{
                                textAlign: `center`,
                                color: RoutalPalette.error.medium,
                              }}
                            />
                          ) : (
                            <Text
                              intlId={`supervisors.create_supervisor.contract_info`}
                              intlValues={{
                                restantes: Math.max(
                                  organizationResources.subscription.supervisors -
                                    organizationResources.getSupervisorsCount() -
                                    1,
                                  0
                                ),
                              }}
                              style={{
                                textAlign: `center`,
                              }}
                            />
                          )
                        ) : (
                          <Text
                            intlId={`supervisors.create_supervisor.summary_info`}
                            intlValues={{
                              price: `${getTotalPrice()}${currencyToSymbol(pricing.currency)} ${
                                organization.subscription.billing_period
                                  ? intl.formatMessage({ id: `subscription.update.per_month` }).toLocaleLowerCase()
                                  : intl.formatMessage({ id: `subscription.update.per_year` }).toLocaleLowerCase()
                              }`,
                            }}
                            style={{
                              textAlign: `center`,
                            }}
                          />
                        )}
                      </Grid>

                      <VSpacer medium />
                    </>
                  )}

                  <Grid item container direction="row" alignItems="center" justifyContent="center">
                    <Grid item>
                      <Button
                        intlId="cancel"
                        variant="flat"
                        styleType="secondary"
                        style={{
                          minWidth: `100px`,
                        }}
                        onClick={() => onClose()}
                      />
                    </Grid>

                    <HSpacer small />

                    <Grid item>
                      <Button
                        disabled={isConfirmButtonDisabled()}
                        style={{
                          height: `16px`,
                          minWidth: `100px`,
                        }}
                        onClick={() => {
                          createSupervisor(supervisorData);
                        }}
                      >
                        {isCreatingSupervisors ? (
                          <CircularProgress size={20} />
                        ) : (
                          <Text variant="button" intlId="create" style={{ color: `white` }} />
                        )}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </>
            ) : null}
          </>
        )}
      </Grid>
    </RoutalDialog>
  );
};
